import React, { useEffect, useState, useMemo, useRef } from 'react';
import { safeArray, unwrapListResponse } from '../../components/ErrorBoundary';
import { useProjects } from '../../context/ProjectsContext';
import LinkedCorrespondenceCard from '../../components/LinkedCorrespondenceCard';
import ProjectFilterSelect from '../../components/ProjectFilterSelect';
import ResetFiltersButton from '../../components/ResetFiltersButton';
import StatusBadge from '../../components/StatusBadge';
import { printTableElement } from '../../utils/exportUtils';
import { milestonesAPI, correspondenceAPI, adminAPI } from '../../services/authService';
import { toStoredAttachments, downloadAttachment, viewAttachmentInNewTab } from '../../utils/attachmentStorage';
import { confirmDialog } from '../../utils/confirmDialog';
import { notifySuccess, notifyError } from '../../utils/notify';
import { formatDateIN } from '../../utils/dateFormat';
import { formatKpiCrores } from '../../utils/kpiAmountFormat';
import { useFormConfiguration } from '../../hooks/useFormConfiguration';
import { ConfiguredField } from '../../components/ConfiguredField';
import { DynamicFieldsSection } from '../../components/DynamicFieldRenderer';

const MILESTONE_STATUS_CLASS_MAP = {
  'On Track': 'on-track',
  'At Risk': 'at-risk',
  Delayed: 'delayed',
  Completed: 'completed',
};

const SHIFT_STATUS_CLASS_MAP = {
  Draft: 'draft',
  Submitted: 'submitted',
  'Under Review': 'under-review',
  Approved: 'approved',
  Rejected: 'rejected',
  Withdrawn: 'closed',
};

const formatCurrency = (value) => (
  `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Number(value || 0))}`
);

const MilestoneTracker = ({ onViewThread }) => {
  const [projectFilter, setProjectFilter] = useState('all');
  const [kpiFilter, setKpiFilter] = useState('all');
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [editingShiftRequest, setEditingShiftRequest] = useState(null);
  const [showEditMilestoneModal, setShowEditMilestoneModal] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [selectedShiftRequest, setSelectedShiftRequest] = useState(null);
  const { projects: projectOptions } = useProjects();
  const [dataReady, setDataReady] = useState(false);
  const [milestones, setMilestones] = useState([]);
  const [shiftRequests, setShiftRequests] = useState([]);
  const [newShiftRequest, setNewShiftRequest] = useState({
    project_id: '',
    milestone_id: '',
    requested_new_date: '',
    reason_category: 'Hindrance',
    impact_days: '',
    remarks: '',
    linked_correspondence: '',
    attachments: [],
  });
  const [editMilestoneForm, setEditMilestoneForm] = useState({
    name: '',
    baseline_date: '',
    revised_date: '',
    actual_date: '',
    status: 'On Track',
  });
  const [correspondenceChains, setCorrespondenceChains] = useState([]);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const showProjectColumn = projectFilter === 'all';
  const tableRef = useRef(null);
  const [customValues, setCustomValues] = useState({});
  const formConfig = useFormConfiguration({ pageKey: 'milestones', formKey: 'eot_request', mode: editingShiftRequest ? 'edit' : 'create' });
  const customFields = formConfig.customFields || [];

  const defaultsFromCustomFields = () => {
    const out = {};
    for (const f of customFields) {
      if (f.defaultValue != null && f.defaultValue !== '') out[f.fieldKey] = f.defaultValue;
      else if (f.fieldType === 'boolean') out[f.fieldKey] = false;
      else out[f.fieldKey] = '';
    }
    return out;
  };

  const onCustomChange = (fieldKey, value) => {
    setCustomValues((prev) => ({ ...prev, [fieldKey]: value }));
  };

  const loadMilestones = React.useCallback(async () => {
    try {
      const response = await milestonesAPI.getAll({ page: 1, pageSize: 200 });
      const { rows: rawRows } = unwrapListResponse(response);
      const rows = safeArray(rawRows)
        .filter((row) => !(row.data || {}).request_no)
        .map((row) => {
          const payload = row.data || {};
          return {
            id: row.id,
            project_id: row.projectId ?? row.project_id ?? 1,
            name: payload.name ?? row.title ?? row.name ?? `Milestone ${row.id}`,
            baseline_date: payload.baseline_date ?? row.baseline_date ?? '',
            revised_date: payload.revised_date ?? row.revised_date ?? '',
            actual_date: payload.actual_date ?? row.actual_date ?? '',
            status: row.status || payload.status || 'On Track',
            shift_requests: payload.shift_requests ?? row.shift_requests ?? 0,
          };
        });
      if (rows.length > 0) setMilestones(rows);
        const shiftRows = safeArray(rawRows)
          .map((row) => {
            const payload = row.data || {};
            if (!payload.request_no) return null;
            return {
              id: row.id,
              project_id: row.projectId ?? row.project_id ?? 1,
              milestone_id: Number(payload.milestone_id || 0),
              request_no: payload.request_no,
              requested_new_date: payload.requested_completion_date || '',
              reason_category: payload.reason_category || 'Other',
              impact_days: Number(payload.eot_days_requested || payload.impact_days || 0),
              financial_impact_applicable: payload.financial_impact_applicable || (Number(payload.estimated_cost_impact || 0) > 0 || Number(payload.ld_risk_impact || 0) > 0 ? 'Yes' : 'No'),
              estimated_cost_impact: Number(payload.estimated_cost_impact || 0),
              ld_risk_impact: Number(payload.ld_risk_impact || 0),
              financial_remarks: payload.financial_remarks || '',
              status: row.status || payload.status || 'Submitted',
              approved_new_date: payload.approved_new_date || null,
              decision_date: payload.decision_date || null,
              approved_by: payload.approved_by || null,
              linked_correspondence: payload.linked_correspondence || null,
              remarks: payload.remarks || '',
              attachments: payload.attachments || [],
            };
          })
          .filter(Boolean);
        if (shiftRows.length > 0) setShiftRequests(shiftRows);
      } catch (error) {
        console.warn('Milestones API unavailable, using local data:', error?.message || error);
      } finally {
        setDataReady(true);
      }
  }, []);

  useEffect(() => {
    loadMilestones();
  }, [loadMilestones]);

  useEffect(() => {
    const loadChains = async () => {
      if (!newShiftRequest.project_id) {
        setCorrespondenceChains([]);
        return;
      }
      try {
        const resp = await correspondenceAPI.getChainsPaginated({
          projectId: newShiftRequest.project_id,
          pageSize: 100,
          page: 1,
        });
        const chains = resp?.data?.chains || [];
        setCorrespondenceChains(Array.isArray(chains) ? chains : []);
      } catch {
        setCorrespondenceChains([]);
      }
    };
    loadChains();
  }, [newShiftRequest.project_id]);

  const projects = safeArray(projectOptions);

  const filteredMilestones = useMemo(() => {
    let ms = [...milestones];
    if (projectFilter !== 'all') ms = ms.filter(m => m.project_id === parseInt(projectFilter));
    return ms.sort((a, b) => Number(b.id || 0) - Number(a.id || 0));
  }, [milestones, projectFilter]);

  const filteredShiftRequests = useMemo(() => {
    let sr = [...shiftRequests];
    if (projectFilter !== 'all') sr = sr.filter(s => s.project_id === parseInt(projectFilter));
    if (kpiFilter === 'financialYes') {
      sr = sr.filter((s) => String(s.financial_impact_applicable || 'No') === 'Yes');
    } else if (kpiFilter === 'costImpact') {
      sr = sr.filter((s) => Number(s.estimated_cost_impact || 0) > 0);
    } else if (kpiFilter === 'ldRisk') {
      sr = sr.filter((s) => Number(s.ld_risk_impact || 0) > 0);
    }
    return sr.sort((a, b) => {
      const da = new Date(a.requested_new_date || a.created_at || 0).getTime();
      const db = new Date(b.requested_new_date || b.created_at || 0).getTime();
      if (db !== da) return db - da;
      return Number(b.id || 0) - Number(a.id || 0);
    });
  }, [projectFilter, shiftRequests, kpiFilter]);

  const shiftFinancialKpis = useMemo(() => {
    const totalRequests = filteredShiftRequests.length;
    const withFinancialImpact = filteredShiftRequests.filter((item) => String(item.financial_impact_applicable || 'No') === 'Yes').length;
    const totalEstimatedCostImpact = filteredShiftRequests.reduce((sum, item) => sum + Number(item.estimated_cost_impact || 0), 0);
    const totalLdRiskExposure = filteredShiftRequests.reduce((sum, item) => sum + Number(item.ld_risk_impact || 0), 0);
    return {
      totalRequests,
      withFinancialImpact,
      totalEstimatedCostImpact,
      totalLdRiskExposure,
    };
  }, [filteredShiftRequests]);

  const getStatusClass = (status) => MILESTONE_STATUS_CLASS_MAP[status] || 'draft';
  const getShiftStatusClass = (status) => SHIFT_STATUS_CLASS_MAP[status] || 'draft';

  if (!dataReady) {
    return (
      <div>
        <div className="section-header"><h2>Milestone/EOT Requests</h2></div>
        <p style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>Loading...</p>
      </div>
    );
  }

  const handleCreateShiftRequest = async () => {
    if (!newShiftRequest.project_id || !newShiftRequest.requested_new_date) {
      notifyError('Project and requested new date are required.');
      return;
    }
    if (formConfig.customFieldsEnabled) {
      for (const f of customFields) {
        if (!f.required || f.hidden) continue;
        const v = customValues[f.fieldKey];
        if (v === undefined || v === null || v === '') {
          notifyError(`${f.label || f.fieldKey} is required.`);
          return;
        }
      }
    }
    if (!(await confirmDialog('Submit this EOT request?', 'New EOT Request'))) return;
    try {
      const payload = {
        projectId: Number(newShiftRequest.project_id),
        title: `EOT Request ${Date.now().toString().slice(-6)}`,
        status: 'Submitted',
        dueDate: newShiftRequest.requested_new_date ? new Date(newShiftRequest.requested_new_date).toISOString() : null,
        data: {
          request_no: `SR-${Date.now().toString().slice(-6)}`,
          milestone_id: Number(newShiftRequest.milestone_id || 0),
          requested_completion_date: newShiftRequest.requested_new_date,
          reason_category: newShiftRequest.reason_category,
          eot_days_requested: Number(newShiftRequest.impact_days || 0),
          remarks: newShiftRequest.remarks || '',
          linked_correspondence: newShiftRequest.linked_correspondence ? Number(newShiftRequest.linked_correspondence) : null,
          attachments: newShiftRequest.attachments || [],
        },
      };
      const response = await milestonesAPI.create(payload);
      const row = response?.data;
      const rowPayload = row.data || {};
      if (formConfig.customFieldsEnabled && row?.id) {
        try {
          await adminAPI.saveCustomFieldValues('Milestone', row.id, {
            pageKey: 'milestones',
            projectId: Number(newShiftRequest.project_id),
            values: customValues,
            mode: 'create',
          });
        } catch (cvErr) {
          console.error('Custom field save failed:', cvErr);
          notifyError(cvErr?.response?.data?.message || 'EOT request saved but custom fields failed.');
        }
      }
      setShiftRequests((prev) => ([
        {
          id: row.id,
          project_id: row.projectId ?? Number(newShiftRequest.project_id),
          milestone_id: Number(rowPayload.milestone_id || newShiftRequest.milestone_id || 0),
          request_no: rowPayload.request_no || `SR-${row.id}`,
          requested_new_date: rowPayload.requested_completion_date || newShiftRequest.requested_new_date,
          reason_category: rowPayload.reason_category || newShiftRequest.reason_category,
          impact_days: Number(rowPayload.eot_days_requested || newShiftRequest.impact_days || 0),
          financial_impact_applicable: rowPayload.financial_impact_applicable || 'No',
          estimated_cost_impact: Number(rowPayload.estimated_cost_impact || 0),
          ld_risk_impact: Number(rowPayload.ld_risk_impact || 0),
          financial_remarks: rowPayload.financial_remarks || '',
          status: row.status || 'Submitted',
          approved_new_date: rowPayload.approved_new_date || null,
          decision_date: rowPayload.decision_date || null,
          approved_by: rowPayload.approved_by || null,
          linked_correspondence: rowPayload.linked_correspondence || null,
          remarks: rowPayload.remarks || newShiftRequest.remarks || '',
          attachments: rowPayload.attachments || [],
        },
        ...prev,
      ]));
      setShowShiftModal(false);
      setEditingShiftRequest(null);
      setNewShiftRequest({
        project_id: '',
        milestone_id: '',
        requested_new_date: '',
        reason_category: 'Hindrance',
        impact_days: '',
        remarks: '',
        linked_correspondence: '',
        attachments: [],
      });
      setCustomValues({});
      notifySuccess('EOT request submitted successfully.');
    } catch (error) {
      console.error('Create EOT request failed:', error);
      notifyError('Unable to create EOT request. Please retry.');
    }
  };

  const handleAttachmentChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    setAttachmentUploading(true);
    try {
      const newFiles = await toStoredAttachments(files);
      setNewShiftRequest((prev) => ({ ...prev, attachments: [...(prev.attachments || []), ...newFiles] }));
    } finally {
      setAttachmentUploading(false);
    }
    event.target.value = '';
  };

  const handleDeleteMilestone = async (ms, e) => {
    e?.stopPropagation?.();
    if (!(await confirmDialog(`Remove milestone "${ms.name}"? This action cannot be undone.`, 'Remove Milestone'))) return;
    try {
      await milestonesAPI.delete(ms.id);
      setMilestones((prev) => prev.filter((m) => m.id !== ms.id));
      notifySuccess('Milestone removed.');
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Unable to remove milestone.';
      notifyError(msg);
    }
  };

  const handleEditMilestone = (ms, e) => {
    e?.stopPropagation?.();
    setEditingMilestone(ms);
    setEditMilestoneForm({
      name: ms.name || '',
      baseline_date: ms.baseline_date || '',
      revised_date: ms.revised_date || '',
      actual_date: ms.actual_date || '',
      status: ms.status || 'On Track',
    });
    setShowEditMilestoneModal(true);
  };

  const handleSaveMilestone = async () => {
    if (!editingMilestone) return;
    try {
      const resp = await milestonesAPI.getById(editingMilestone.id);
      const row = resp?.data || {};
      const existingData = row.data || {};
      await milestonesAPI.update(editingMilestone.id, {
        title: editMilestoneForm.name,
        status: editMilestoneForm.status,
        dueDate: editMilestoneForm.revised_date || editMilestoneForm.baseline_date || null,
        data: {
          ...existingData,
          name: editMilestoneForm.name,
          baseline_date: editMilestoneForm.baseline_date || null,
          revised_date: editMilestoneForm.revised_date || null,
          actual_date: editMilestoneForm.actual_date || null,
        },
      });
      setMilestones((prev) =>
        prev.map((m) =>
          m.id === editingMilestone.id
            ? {
                ...m,
                name: editMilestoneForm.name,
                baseline_date: editMilestoneForm.baseline_date,
                revised_date: editMilestoneForm.revised_date,
                actual_date: editMilestoneForm.actual_date,
                status: editMilestoneForm.status,
              }
            : m
        )
      );
      setShowEditMilestoneModal(false);
      setEditingMilestone(null);
      notifySuccess('Milestone updated.');
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Unable to update milestone.');
    }
  };

  const handleDeleteShiftRequest = async (sr, e) => {
    e?.stopPropagation?.();
    if (!(await confirmDialog(`Remove EOT request "${sr.request_no}"? This action cannot be undone.`, 'Remove EOT Request'))) return;
    try {
      await milestonesAPI.delete(sr.id);
      setShiftRequests((prev) => prev.filter((s) => s.id !== sr.id));
      setSelectedShiftRequest(null);
      setEditingShiftRequest(null);
      notifySuccess('EOT request removed.');
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Unable to remove EOT request.';
      notifyError(msg);
    }
  };

  const handleEditShiftRequest = async (sr, e) => {
    e?.stopPropagation?.();
    setEditingShiftRequest(sr);
    setNewShiftRequest({
      project_id: String(sr.project_id || ''),
      milestone_id: String(sr.milestone_id || ''),
      requested_new_date: sr.requested_new_date || '',
      reason_category: sr.reason_category || 'Hindrance',
      impact_days: String(sr.impact_days || ''),
      remarks: sr.remarks || '',
      linked_correspondence: sr.linked_correspondence ? String(sr.linked_correspondence) : '',
      attachments: sr.attachments || [],
    });
    setCustomValues(defaultsFromCustomFields());
    if (formConfig.customFieldsEnabled) {
      try {
        const res = await adminAPI.getCustomFieldValues('Milestone', sr.id);
        setCustomValues({
          ...defaultsFromCustomFields(),
          ...(res?.data?.data?.values || res?.data?.values || {}),
        });
      } catch (_e) {
        /* keep defaults */
      }
    }
    setShowShiftModal(true);
  };

  const handleSaveShiftRequest = async () => {
    if (!editingShiftRequest) return handleCreateShiftRequest();
    if (formConfig.customFieldsEnabled) {
      for (const f of customFields) {
        if (!f.required || f.hidden) continue;
        const v = customValues[f.fieldKey];
        if (v === undefined || v === null || v === '') {
          notifyError(`${f.label || f.fieldKey} is required.`);
          return;
        }
      }
    }
    try {
      const resp = await milestonesAPI.getById(editingShiftRequest.id);
      const row = resp?.data || {};
      const existingData = row.data || {};
      await milestonesAPI.update(editingShiftRequest.id, {
        title: row.title,
        status: row.status,
        dueDate: newShiftRequest.requested_new_date ? new Date(newShiftRequest.requested_new_date).toISOString() : null,
        data: {
          ...existingData,
          milestone_id: Number(newShiftRequest.milestone_id || 0),
          requested_completion_date: newShiftRequest.requested_new_date,
          reason_category: newShiftRequest.reason_category,
          eot_days_requested: Number(newShiftRequest.impact_days || 0),
          remarks: newShiftRequest.remarks || '',
          linked_correspondence: newShiftRequest.linked_correspondence ? Number(newShiftRequest.linked_correspondence) : null,
          attachments: newShiftRequest.attachments || [],
        },
      });
      if (formConfig.customFieldsEnabled) {
        try {
          await adminAPI.saveCustomFieldValues('Milestone', editingShiftRequest.id, {
            pageKey: 'milestones',
            projectId: Number(newShiftRequest.project_id),
            values: customValues,
            mode: 'edit',
          });
        } catch (cvErr) {
          console.error('Custom field save failed:', cvErr);
          notifyError(cvErr?.response?.data?.message || 'EOT request saved but custom fields failed.');
        }
      }
      setShiftRequests((prev) =>
        prev.map((s) =>
          s.id === editingShiftRequest.id
            ? {
                ...s,
                project_id: Number(newShiftRequest.project_id),
                milestone_id: Number(newShiftRequest.milestone_id || 0),
                requested_new_date: newShiftRequest.requested_new_date,
                reason_category: newShiftRequest.reason_category,
                impact_days: Number(newShiftRequest.impact_days || 0),
                remarks: newShiftRequest.remarks || '',
                linked_correspondence: newShiftRequest.linked_correspondence ? Number(newShiftRequest.linked_correspondence) : null,
                attachments: newShiftRequest.attachments || [],
              }
            : s
        )
      );
      setShowShiftModal(false);
      setEditingShiftRequest(null);
      setNewShiftRequest({
        project_id: '',
        milestone_id: '',
        requested_new_date: '',
        reason_category: 'Hindrance',
        impact_days: '',
        remarks: '',
        linked_correspondence: '',
        attachments: [],
      });
      setCustomValues({});
      notifySuccess('EOT request updated.');
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Unable to update EOT request.');
    }
  };

  const handleShiftModalClose = () => {
    setShowShiftModal(false);
    setEditingShiftRequest(null);
    setNewShiftRequest({
      project_id: '',
      milestone_id: '',
      requested_new_date: '',
      reason_category: 'Hindrance',
      impact_days: '',
      remarks: '',
      linked_correspondence: '',
      attachments: [],
    });
    setCustomValues({});
  };

  return (
    <div>
      <div className="section-header">
        <h2>Milestone/EOT Requests</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn-secondary"
            onClick={() => printTableElement({ title: 'Milestone/EOT Requests', subtitle: `Project: ${projectFilter === 'all' ? 'All Projects' : (projects.find(p => p.id === parseInt(projectFilter, 10))?.name || 'Selected Project')}`, element: tableRef.current })}
          >
            Download PDF
          </button>
          <button className="btn-primary" onClick={() => { setEditingShiftRequest(null); setNewShiftRequest({ project_id: '', milestone_id: '', requested_new_date: '', reason_category: 'Hindrance', impact_days: '', remarks: '', linked_correspondence: '', attachments: [] }); setCustomValues(defaultsFromCustomFields()); setShowShiftModal(true); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            New EOT Request
          </button>
        </div>
      </div>

      <div className="filter-bar">
        <ProjectFilterSelect
          value={projectFilter}
          onChange={setProjectFilter}
          projects={projects}
        />
        <ResetFiltersButton
          onClick={() => {
            setProjectFilter('all');
            setKpiFilter('all');
          }}
        />
      </div>

      <div className="cards-grid kpi-cards" style={{ marginBottom: 16 }}>
        <div className="card" onClick={() => setKpiFilter('all')} style={{ cursor: 'pointer', borderColor: kpiFilter === 'all' ? '#93c5fd' : undefined }}>
          <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Total EOT Requests</p>
          <p className="kpi-value">{shiftFinancialKpis.totalRequests}</p>
        </div>
        <div className="card" onClick={() => setKpiFilter('financialYes')} style={{ cursor: 'pointer', borderColor: kpiFilter === 'financialYes' ? '#93c5fd' : undefined }}>
          <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>With Financial Impact</p>
          <p className="kpi-value">{shiftFinancialKpis.withFinancialImpact}</p>
        </div>
        <div className="card" onClick={() => setKpiFilter('costImpact')} style={{ cursor: 'pointer', borderColor: kpiFilter === 'costImpact' ? '#93c5fd' : undefined }}>
          <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Estimated Cost Impact</p>
          <p className="kpi-value">{formatKpiCrores(shiftFinancialKpis.totalEstimatedCostImpact)}</p>
        </div>
        <div className="card" onClick={() => setKpiFilter('ldRisk')} style={{ cursor: 'pointer', borderColor: kpiFilter === 'ldRisk' ? '#93c5fd' : undefined }}>
          <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>LD Risk Exposure</p>
          <p className="kpi-value">{formatKpiCrores(shiftFinancialKpis.totalLdRiskExposure)}</p>
        </div>
      </div>

      <div className="table-container" style={{ marginBottom: 16 }} ref={tableRef}>
        <h3 style={{ marginBottom: 12, fontSize: 16 }}>EOT Requests ({filteredShiftRequests.length})</h3>
        <table className="data-table">
          <thead>
            <tr>
              <th>Reference</th>
              {showProjectColumn && <th>Project</th>}
              <th>Milestone</th>
              <th>Requested Date</th>
              <th>Approved Date</th>
              <th>Status</th>
              <th>Impact Days</th>
              <th className="amount-cell">Est. Cost Impact</th>
              <th className="amount-cell">LD Risk</th>
              <th>Correspondence</th>
              <th>Reason</th>
              <th>Decision</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredShiftRequests.length === 0 ? (
              <tr>
                <td colSpan={12 + (showProjectColumn ? 1 : 0)} style={{ textAlign: 'center', color: '#6b7280', padding: 24 }}>
                  No EOT requests found.
                </td>
              </tr>
            ) : filteredShiftRequests.map((sr) => {
              const milestone = milestones.find((m) => m.id === sr.milestone_id);
              const project = projects.find((p) => p.id === sr.project_id);
              return (
                <tr key={`sr-${sr.id}`} className="clickable" onClick={() => setSelectedShiftRequest(sr)}>
                  <td style={{ fontWeight: 500, color: '#7C3AED' }}>{sr.request_no}</td>
                  {showProjectColumn && <td style={{ fontSize: 12, color: '#6b7280' }}>{project?.name || '-'}</td>}
                  <td style={{ fontSize: 12 }}>{milestone?.name || '-'}</td>
                  <td>{formatDateIN(sr.requested_new_date)}</td>
                  <td>{formatDateIN(sr.approved_new_date)}</td>
                  <td><StatusBadge tone={getShiftStatusClass(sr.status)}>{sr.status}</StatusBadge></td>
                  <td style={{ fontWeight: 500, color: sr.impact_days > 30 ? '#b91c1c' : '#374151' }}>{sr.impact_days}</td>
                  <td className="amount-cell" style={{ fontWeight: 500 }}>{formatCurrency(sr.estimated_cost_impact || 0)}</td>
                  <td className="amount-cell" style={{ fontWeight: 500 }}>{formatCurrency(sr.ld_risk_impact || 0)}</td>
                  <td>
                    {sr.linked_correspondence && onViewThread ? (
                      <span
                        role="button"
                        tabIndex={0}
                        className="link-style"
                        onClick={(e) => { e.stopPropagation(); onViewThread(sr.linked_correspondence); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onViewThread(sr.linked_correspondence); } }}
                        style={{ cursor: 'pointer', color: '#7C3AED', textDecoration: 'underline', textUnderlineOffset: 2 }}
                      >
                        Open
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td title={sr.reason_category || ''}>
                    <StatusBadge tone={sr.reason_category === 'Hindrance' ? 'critical' : sr.reason_category === 'Drawing delay' ? 'high' : 'medium'}>
                      {sr.reason_category || '-'}
                    </StatusBadge>
                  </td>
                  <td style={{ fontSize: 12, color: '#6b7280' }}>
                    {sr.decision_date ? `${formatDateIN(sr.decision_date)} by ${sr.approved_by}` : '-'}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <button type="button" className="btn-secondary btn-sm" onClick={(e) => handleEditShiftRequest(sr, e)} title="Edit">Edit</button>
                      <button type="button" className="btn-secondary btn-sm" style={{ color: '#b91c1c' }} onClick={(e) => handleDeleteShiftRequest(sr, e)} title="Remove">Remove</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="table-container">
        <h3 style={{ marginBottom: 12, fontSize: 16 }}>Milestones ({filteredMilestones.length})</h3>
        <table className="data-table">
          <thead>
            <tr>
              <th>Milestone</th>
              {showProjectColumn && <th>Project</th>}
              <th>Baseline Date</th>
              <th>Revised Date</th>
              <th>Actual Date</th>
              <th>Status</th>
              <th>EOT Count</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredMilestones.length === 0 ? (
              <tr>
                <td colSpan={7 + (showProjectColumn ? 1 : 0)} style={{ textAlign: 'center', color: '#6b7280', padding: 24 }}>
                  No milestones found.
                </td>
              </tr>
            ) : filteredMilestones.map((ms) => {
              const project = projects.find((p) => p.id === ms.project_id);
              return (
                <tr key={`ms-${ms.id}`}>
                  <td style={{ fontWeight: 500 }}>{ms.name}</td>
                  {showProjectColumn && <td style={{ fontSize: 12, color: '#6b7280' }}>{project?.name || '-'}</td>}
                  <td>{formatDateIN(ms.baseline_date)}</td>
                  <td style={{ color: ms.revised_date !== ms.baseline_date ? '#b45309' : '#374151' }}>
                    {formatDateIN(ms.revised_date)}
                    {ms.revised_date !== ms.baseline_date && (
                      <span style={{ fontSize: 11, color: '#ef4444', marginLeft: 4 }}>
                        (+{Math.ceil((new Date(ms.revised_date) - new Date(ms.baseline_date)) / (1000 * 60 * 60 * 24))}d)
                      </span>
                    )}
                  </td>
                  <td>{formatDateIN(ms.actual_date)}</td>
                  <td><StatusBadge tone={getStatusClass(ms.status)}>{ms.status}</StatusBadge></td>
                  <td>{ms.shift_requests ?? 0}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <button type="button" className="btn-secondary btn-sm" onClick={(e) => handleEditMilestone(ms, e)} title="Edit">Edit</button>
                      <button type="button" className="btn-secondary btn-sm" style={{ color: '#b91c1c' }} onClick={(e) => handleDeleteMilestone(ms, e)} title="Remove">Remove</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showShiftModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingShiftRequest ? 'Edit EOT Request' : 'New Milestone EOT Request'}</h2>
              <button className="btn-close" onClick={handleShiftModalClose}>
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              {!formConfig.fallback && (
                <p style={{ fontSize: 12, color: '#64748b', marginTop: 0 }}>Using published form configuration v{formConfig.config?.versionNumber || '—'}</p>
              )}
              <div className="form-row">
                <ConfiguredField fieldKey="project_id" formConfig={formConfig} defaultLabel="Project" defaultRequired>
                  {({ readOnly }) => (
                    <select value={newShiftRequest.project_id} disabled={readOnly} onChange={(e) => setNewShiftRequest((prev) => ({ ...prev, project_id: e.target.value }))}><option value="">Select Project</option>{projects.filter(p => p.status === 'Active').map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
                  )}
                </ConfiguredField>
                <ConfiguredField fieldKey="milestone_id" formConfig={formConfig} defaultLabel="Milestone">
                  {({ readOnly }) => (
                    <select value={newShiftRequest.milestone_id} disabled={readOnly} onChange={(e) => setNewShiftRequest((prev) => ({ ...prev, milestone_id: e.target.value }))}>
                      <option value="">Select Milestone</option>
                      {milestones.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                  )}
                </ConfiguredField>
              </div>
              <div className="form-row">
                <ConfiguredField fieldKey="requested_new_date" formConfig={formConfig} defaultLabel="Requested New Date" defaultRequired>
                  {({ readOnly }) => (
                    <input type="date" value={newShiftRequest.requested_new_date} disabled={readOnly} onChange={(e) => setNewShiftRequest((prev) => ({ ...prev, requested_new_date: e.target.value }))} />
                  )}
                </ConfiguredField>
                <ConfiguredField fieldKey="reason_category" formConfig={formConfig} defaultLabel="Reason Category">
                  {({ readOnly }) => (
                    <select value={newShiftRequest.reason_category} disabled={readOnly} onChange={(e) => setNewShiftRequest((prev) => ({ ...prev, reason_category: e.target.value }))}>
                      <option value="Hindrance">Hindrance</option>
                      <option value="Drawing delay">Drawing Delay</option>
                      <option value="Client decision">Client Decision</option>
                      <option value="Design change">Design Change</option>
                      <option value="Force majeure">Force Majeure</option>
                      <option value="Utility shifting">Utility Shifting</option>
                      <option value="Other">Other</option>
                    </select>
                  )}
                </ConfiguredField>
              </div>
              <ConfiguredField fieldKey="impact_days" formConfig={formConfig} defaultLabel="Impact Days">
                {({ readOnly, placeholder }) => (
                  <input type="number" placeholder={placeholder || '0'} value={newShiftRequest.impact_days} disabled={readOnly} onChange={(e) => setNewShiftRequest((prev) => ({ ...prev, impact_days: e.target.value }))} />
                )}
              </ConfiguredField>
              <div className="form-group">
                <label>Link Letter / Email Thread</label>
                <select
                  value={newShiftRequest.linked_correspondence || ''}
                  onChange={(e) => setNewShiftRequest((prev) => ({ ...prev, linked_correspondence: e.target.value }))}
                >
                  <option value="">None</option>
                  {correspondenceChains.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.first_letter_ref || `#${c.id}`} - {c.subject}
                    </option>
                  ))}
                </select>
                {!newShiftRequest.project_id && (
                  <p style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>Select a project first to see correspondence.</p>
                )}
              </div>
              <ConfiguredField fieldKey="remarks" formConfig={formConfig} defaultLabel="Remarks">
                {({ readOnly, placeholder }) => (
                  <textarea placeholder={placeholder || 'Justification for the EOT request...'} rows={3} value={newShiftRequest.remarks} disabled={readOnly} onChange={(e) => setNewShiftRequest((prev) => ({ ...prev, remarks: e.target.value }))} />
                )}
              </ConfiguredField>
              {formConfig.customFieldsEnabled && (
                <DynamicFieldsSection
                  fields={customFields}
                  values={customValues}
                  onChange={onCustomChange}
                  title="Additional fields"
                />
              )}
              {!formConfig.isHidden('attachments') && (
              <div className="form-group">
                <label>{formConfig.labelFor('attachments', 'Attachments')}</label>
                <input type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" onChange={handleAttachmentChange} disabled={attachmentUploading} />
                {attachmentUploading && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 13, color: '#6b7280' }}>
                    <span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid #e5e7eb', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    Processing upload...
                  </span>
                )}
                {newShiftRequest.attachments?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                    {newShiftRequest.attachments.map((file, idx) => (
                      <span key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '4px 8px' }}>
                        {file?.name}
                        <span style={{ color: '#22c55e' }}>✓</span>
                        <button type="button" className="btn-secondary btn-sm" onClick={() => setNewShiftRequest((prev) => ({ ...prev, attachments: (prev.attachments || []).filter((_, i) => i !== idx) }))}>✕</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              )}
              <div className="modal-actions">
                <button className="btn-secondary" onClick={handleShiftModalClose}>Cancel</button>
                <button className="btn-primary" onClick={editingShiftRequest ? handleSaveShiftRequest : handleCreateShiftRequest} disabled={attachmentUploading}>
                  {editingShiftRequest ? 'Save Changes' : 'Submit Request'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditMilestoneModal && editingMilestone && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Edit Milestone</h2>
              <button className="btn-close" onClick={() => { setShowEditMilestoneModal(false); setEditingMilestone(null); }}>
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Name</label>
                <input type="text" value={editMilestoneForm.name} onChange={(e) => setEditMilestoneForm((p) => ({ ...p, name: e.target.value }))} placeholder="Milestone name" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Baseline Date</label>
                  <input type="date" value={editMilestoneForm.baseline_date} onChange={(e) => setEditMilestoneForm((p) => ({ ...p, baseline_date: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Revised Date</label>
                  <input type="date" value={editMilestoneForm.revised_date} onChange={(e) => setEditMilestoneForm((p) => ({ ...p, revised_date: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Actual Date</label>
                  <input type="date" value={editMilestoneForm.actual_date} onChange={(e) => setEditMilestoneForm((p) => ({ ...p, actual_date: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select value={editMilestoneForm.status} onChange={(e) => setEditMilestoneForm((p) => ({ ...p, status: e.target.value }))}>
                    <option value="On Track">On Track</option>
                    <option value="At Risk">At Risk</option>
                    <option value="Delayed">Delayed</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => { setShowEditMilestoneModal(false); setEditingMilestone(null); }}>Cancel</button>
                <button className="btn-primary" onClick={handleSaveMilestone}>Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedShiftRequest && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>EOT Request Detail - {selectedShiftRequest.request_no}</h2>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginLeft: 'auto' }}>
                <button className="btn-secondary btn-sm" onClick={(e) => { handleEditShiftRequest(selectedShiftRequest, e); setSelectedShiftRequest(null); }}>Edit</button>
                <button className="btn-secondary btn-sm" style={{ color: '#b91c1c' }} onClick={(e) => { handleDeleteShiftRequest(selectedShiftRequest, e); }}>Remove</button>
                <button className="btn-close" onClick={() => setSelectedShiftRequest(null)}>
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="modal-body">
              <LinkedCorrespondenceCard linkedCorrespondenceId={selectedShiftRequest.linked_correspondence} />
              {selectedShiftRequest.linked_correspondence && onViewThread && (
                <div style={{ marginBottom: 16 }}>
                  <button
                    type="button"
                    className="btn-primary btn-sm"
                    onClick={() => {
                      onViewThread(selectedShiftRequest.linked_correspondence);
                      setSelectedShiftRequest(null);
                    }}
                  >
                    View in Correspondence Register
                  </button>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Request No</p><p style={{ fontSize: 14, fontWeight: 500 }}>{selectedShiftRequest.request_no || '-'}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Status</p><p><StatusBadge tone={getShiftStatusClass(selectedShiftRequest.status)}>{selectedShiftRequest.status}</StatusBadge></p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Requested New Date</p><p style={{ fontSize: 14 }}>{formatDateIN(selectedShiftRequest.requested_new_date)}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Impact Days</p><p style={{ fontSize: 14 }}>{selectedShiftRequest.impact_days || 0}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Estimated Cost Impact</p><p style={{ fontSize: 14, fontWeight: 500 }}>{formatCurrency(selectedShiftRequest.estimated_cost_impact || 0)}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>LD Risk Exposure</p><p style={{ fontSize: 14, fontWeight: 500 }}>{formatCurrency(selectedShiftRequest.ld_risk_impact || 0)}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Reason Category</p><p style={{ fontSize: 14 }}>{selectedShiftRequest.reason_category || '-'}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Decision</p><p style={{ fontSize: 14 }}>{selectedShiftRequest.decision_date ? `${formatDateIN(selectedShiftRequest.decision_date)} by ${selectedShiftRequest.approved_by || '-'}` : '-'}</p></div>
              </div>
              {(selectedShiftRequest.attachments || []).length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>Attachments</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                    {selectedShiftRequest.attachments.map((file, idx) => (
                      <span key={idx} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <span style={{ fontSize: 13, color: '#374151' }}>{file?.name || `Attachment ${idx + 1}`}</span>
                        <button type="button" className="btn-secondary btn-sm" onClick={() => viewAttachmentInNewTab(file, () => downloadAttachment(file, `EOT request attachment: ${file?.name}`))}>View</button>
                        <button type="button" className="btn-secondary btn-sm" onClick={() => downloadAttachment(file, `EOT request attachment: ${file?.name}`)}>Download</button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {selectedShiftRequest.remarks && (
                <div style={{ marginTop: 16 }}>
                  <p style={{ fontSize: 12, color: '#6b7280' }}>Remarks</p>
                  <p style={{ fontSize: 13, color: '#374151' }}>{selectedShiftRequest.remarks}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MilestoneTracker;
